import React from 'react';
import { i18n } from 'config/locales';
import ValidationElement from 'components/Form/ValidationElement';
import Field from 'components/Form/Field';
import Textarea from 'components/Form/Textarea';
import DateRange from 'components/Form/DateRange';
import Text from 'components/Form/Text';
import { SubstanceDrugsPublicSafetyItemModel } from 'forms/sections/substance/drugs/publicSafety/types';
import {
  CommonOnUpdate, CommonScrollIntoView, CommonOnError, CommonRequired
} from 'types/props';
import { ValidationElementProps } from 'components/Form/ValidationElement/ValidationElement';
import Comments from 'components/Form/Comments';
import { isPVQForm } from 'helpers/form';
import Dropdown from 'components/Form/Dropdown';
import { criminalJusticeorPublicSafetyPosition } from 'constants/enums/substanceOptions';
import DrugType from './DrugType';

interface Props extends Partial<SubstanceDrugsPublicSafetyItemModel>, ValidationElementProps {
  onUpdate?: CommonOnUpdate
  scrollIntoView?: CommonScrollIntoView
  onError?: CommonOnError
  required?: CommonRequired
  formType?: string
}

export default class DrugPublicSafetyUse extends ValidationElement<Props> {
  update = (updateValues: Partial<SubstanceDrugsPublicSafetyItemModel>) => {
    if (this.props.onUpdate) {
      this.props.onUpdate({
        Description: this.props.Description,
        InvolvementDates: this.props.InvolvementDates,
        EstimatedUse: this.props.EstimatedUse,
        Comments: this.props.Comments,
        UseWhileEmployedPosition: this.props.UseWhileEmployedPosition, // PVQOnly
        UseWhileEmployedPositionOther: this.props.UseWhileEmployedPositionOther, // PVQOnly
        DrugType: this.props.DrugType, // PVQ Only
        DrugTypeOther: this.props.DrugTypeOther, // PVQOnly
        HowManyTimes: this.props.HowManyTimes, // PVQOnly
        Reason: this.props.Reason, // PVQOnly
        ...updateValues,
      });
    }
  };

  updateDescription = (values: SubstanceDrugsPublicSafetyItemModel['Description']) => {
    this.update({ Description: values });
  };

  updateInvolvementDates = (values: SubstanceDrugsPublicSafetyItemModel['InvolvementDates']) => {
    this.update({ InvolvementDates: values });
  };

  updateEstimatedUse = (values: SubstanceDrugsPublicSafetyItemModel['EstimatedUse']) => {
    this.update({ EstimatedUse: values });
  };

  updateComments = (values: SubstanceDrugsPublicSafetyItemModel['Comments']) => {
    this.update({ Comments: values });
  };

  updateUseWhileEmployedPosition = (values: SubstanceDrugsPublicSafetyItemModel['UseWhileEmployedPosition']) => {
    this.update({ UseWhileEmployedPosition: values });
  };

  updateUseWhileEmployedPositionOther = (values: SubstanceDrugsPublicSafetyItemModel['UseWhileEmployedPositionOther']) => {
    this.update({ UseWhileEmployedPositionOther: values });
  };

  updateDrugType = (values: SubstanceDrugsPublicSafetyItemModel['DrugType']) => {
    this.update({ DrugType: values });
  };

  updateDrugTypeOther = (values: SubstanceDrugsPublicSafetyItemModel['DrugTypeOther']) => {
    this.update({ DrugTypeOther: values });
  };

  updateHowManyTimes = (values: SubstanceDrugsPublicSafetyItemModel['HowManyTimes']) => {
    this.update({ HowManyTimes: values });
  };

  updateReason = (values: SubstanceDrugsPublicSafetyItemModel['Reason']) => {
    this.update({ Reason: values });
  };

  render() {
    const attribute = `List.items[${this.props.$index}].Item`;

    const isPvq = isPVQForm(this.props.formType);
    return isPvq ? (
      <div className="drug-public-safety-use">
        <Field
          title={i18n.t('substance.pvq.drugs.publicSafety.heading.drugType')}
          className="drug-type-use"
          attribute={`${attribute}.DrugType`}
          adjustFor="labels"
          scrollIntoView={this.props.scrollIntoView}
        >
          <DrugType
            {...this.props.DrugType}
            attribute={attribute}
            name="DrugType"
            onUpdate={this.updateDrugType}
            onError={this.props.onError}
            required={this.props.required}
            scrollIntoView={this.props.scrollIntoView}
            formType={this.props.formType}
          />
        </Field>

        {this.props.DrugType?.value === 'Other' && (

          <Field
            title={i18n.t('substance.pvq.drugs.publicSafety.heading.otherDrugType')}
            attribute={`${attribute}.DrugTypeOther`}
            scrollIntoView={this.props.scrollIntoView}
          >
            <Textarea
              {...this.props.DrugTypeOther}
              name="DrugTypeOther"
              attribute={`${attribute}.DrugTypeOther`}
              className="drugTypeOther"
              onUpdate={this.updateDrugTypeOther}
              onError={this.props.onError}
              required={this.props.required}
            />
          </Field>
        )}

        <Field
          attribute={`${attribute}.InvolvementDates`}
          adjustFor="daterange"
          scrollIntoView={this.props.scrollIntoView}
        >
          <DateRange
            {...this.props.InvolvementDates}
            attribute={`${attribute}.InvolvementDates`}
            className="involvement-dates"
            fromDateLabel={i18n.t('substance.pvq.drugs.publicSafety.heading.involvementDatesFrom')}
            hideFromDay
            hideToDay
            minDateEqualTo
            name="InvolvementDates"
            onError={this.props.onError}
            onUpdate={this.updateInvolvementDates}
            required={this.props.required}
            toDateLabel={i18n.t('substance.pvq.drugs.publicSafety.heading.involvementDatesTo')}
            usePvqFromDate
            usePvqToDate
            allowPresent={false}
          />
        </Field>
        <Field
          title={i18n.t('substance.pvq.drugs.publicSafety.heading.howManyTimes')}
          attribute={`${attribute}.HowManyTimes`}
          scrollIntoView={this.props.scrollIntoView}
        >
          <Text
            {...this.props.HowManyTimes}
            name="HowManyTimes"
            attribute={`${attribute}.HowManyTimes`}
            className="howManyTimes"
            onUpdate={this.updateHowManyTimes}
            onError={this.props.onError}
            required={this.props.required}
          />
        </Field>

        <Field
          title={i18n.t('substance.pvq.drugs.publicSafety.heading.useWhileEmployedPosition')}
          attribute={`${attribute}.UseWhileEmployedPosition`}
          adjustFor="buttons"
          shrink
          className="no-margin-bottom"
        >
          <Dropdown
            name="UseWhileEmployedPosition"
            attribute={`${attribute}.UseWhileEmployedPosition`}
            className="UseWhileEmployedPosition"
            onUpdate={this.updateUseWhileEmployedPosition}
            onError={this.props.onError}
            value={this.props.UseWhileEmployedPosition?.value}
            required={this.props.required}
          >
            {criminalJusticeorPublicSafetyPosition.map((s) => (
              <option key={s} value={s}>
                {s}
              </option>
            ))}
          </Dropdown>
        </Field>

        {this.props.UseWhileEmployedPosition?.value === 'Other' && ( // Note: This field has no title in the JSON / no way of the user knowing to type other
          <Field
            title={i18n.t('substance.pvq.drugs.publicSafety.heading.useWhileEmployedPositionExplanation')}
            attribute={`${attribute}.UseWhileEmployedPositionOther`}
            scrollIntoView={this.props.scrollIntoView}
          >
            <Textarea
              {...this.props.UseWhileEmployedPositionOther}
              name="UseWhileEmployedPositionOther"
              attribute={`${attribute}.UseWhileEmployedPositionOther`}
              className="UseWhileEmployedPositionOther"
              onUpdate={this.updateUseWhileEmployedPositionOther}
              onError={this.props.onError}
              required={this.props.required}
            />
          </Field>
        )}

        <Field
          title={i18n.t('substance.pvq.drugs.publicSafety.heading.reason')}
          attribute={`${attribute}.Reason`}
          scrollIntoView={this.props.scrollIntoView}
        >
          <Textarea
            {...this.props.Reason}
            name="Reason"
            attribute={`${attribute}.Reason`}
            className="reason"
            onUpdate={this.updateReason}
            onError={this.props.onError}
            required={this.props.required}
          />
        </Field>
      </div>
    ) : (
      <div className="drug-public-safety-use">
        <Field
          attribute={`${attribute}.Description`}
          title={i18n.t('substance.drugs.publicSafety.heading.description')}
          scrollIntoView={this.props.scrollIntoView}
        >
          <Textarea
            attribute={`${attribute}.Description`}
            name="Description"
            className="description"
            {...this.props.Description}
            onUpdate={this.updateDescription}
            onError={this.props.onError}
            required={this.props.required}
          />
        </Field>

        <Field
          attribute={`${attribute}.InvolvementDates`}
          title={i18n.t('substance.drugs.publicSafety.heading.involvementDates')}
          adjustFor="daterange"
          help="substance.drugs.publicSafety.help.involvementDates"
          scrollIntoView={this.props.scrollIntoView}
        >
          <DateRange
            attribute={`${attribute}.InvolvementDates`}
            name="InvolvementDates"
            className="involvement-dates"
            minDateEqualTo
            {...this.props.InvolvementDates}
            onUpdate={this.updateInvolvementDates}
            onError={this.props.onError}
            required={this.props.required}
            hideFromDay
            hideToDay
          />
        </Field>

        <Field
          attribute={`${attribute}.EstimatedUse`}
          title={i18n.t('substance.drugs.publicSafety.heading.estimatedUse')}
          scrollIntoView={this.props.scrollIntoView}
        >
          <Text
            attribute={`${attribute}.EstimatedUse`}
            name="EstimatedUse"
            className="estimated-use"
            {...this.props.EstimatedUse}
            onUpdate={this.updateEstimatedUse}
            onError={this.props.onError}
            required={this.props.required}
          />
        </Field>
        <Comments
          title={i18n.t('substance.comments.optional')}
          name="Comments"
          className="optional-comment"
          attribute="Comments"
          {...this.props.Comments}
          onUpdate={this.updateComments}
          required={false}
        />
      </div>
    );
  }
}

DrugPublicSafetyUse.defaultProps = {
  onError: (value, arr) => arr,
  Comments: {},
  formType: '',
  UseWhileEmployedPosition: {}, // PVQOnly
  UseWhileEmployedPositionOther: {}, // PVQOnly
  DrugType: {}, // PVQ Only
  DrugTypeOther: {}, // PVQOnly
  HowManyTimes: {}, // PVQOnly
  Reason: {}, // PVQOnly
};
